import React from 'react';
import { Sparkles, Dumbbell, Ticket, Clock, Stethoscope, Check, ArrowRight } from 'lucide-react';

const STEPS = [
  {
    Icon: Sparkles,
    accent: 'from-teal-500 to-emerald-600',
    eyebrow: 'BEM-VINDO',
    title: 'Vital Personal',
    desc: 'O ecossistema mais completo de saúde e performance do Brasil.',
  },
  {
    Icon: Dumbbell,
    accent: 'from-blue-500 to-indigo-600',
    eyebrow: 'ACADEMIAS',
    title: 'Check-ins ∞',
    desc: 'Treine quantas vezes quiser, em qualquer academia parceira. Sem limites.',
    highlight: '∞',
  },
  {
    Icon: Ticket,
    accent: 'from-teal-500 to-emerald-600',
    eyebrow: 'VITALPASS',
    title: '1 VITALPASS = 1 atendimento',
    desc: 'Use seus tokens para agendar Personal, Fisio, Nutri, Psi, Estética ou Massagem.',
    highlight: '1 → 1',
  },
  {
    Icon: Clock,
    accent: 'from-amber-500 to-orange-600',
    eyebrow: 'CICLO 30 DIAS',
    title: 'Renovação mensal',
    desc: 'Seus VITALPASS renovam a cada 30 dias. Use antes de expirar.',
    highlight: '30d',
  },
  {
    Icon: Stethoscope,
    accent: 'from-emerald-500 to-teal-600',
    eyebrow: 'É SIMPLES',
    title: 'Em 3 passos',
    desc: '1. Escolha o profissional  •  2. Confirme  •  3. Realize seu atendimento.',
  },
];

const LandingVitalPassWalkthrough = ({ onCreateAccount }) => {
  return (
    <section
      className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-white via-teal-50/40 to-white"
      data-testid="landing-vitalpass-walkthrough"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-sm font-bold uppercase tracking-widest text-teal-600 mb-2 flex items-center justify-center gap-2">
            <Ticket className="w-4 h-4" /> COMO FUNCIONA O VITALPASS
          </p>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Academias ilimitadas <span className="text-teal-600">+ profissionais parceiros</span>
          </h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
            Entenda em 30 segundos como o plano Vital Personal transforma sua rotina de saúde
            em uma experiência premium — sem cotas na rede de academias e com atendimentos
            profissionais inclusos.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((s, i) => (
            <div
              key={i}
              data-testid={`walkthrough-step-${i}`}
              className="relative bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              {/* Icon block with gradient */}
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.accent} flex items-center justify-center mb-4 shadow-sm`}>
                <s.Icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                {s.eyebrow}
              </p>
              <h3
                className="text-lg font-black text-slate-900 leading-tight mb-1"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {s.title}
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>

              {s.highlight && (
                <div className="mt-3 inline-block bg-slate-900 text-white text-xs font-black px-3 py-1 rounded-full">
                  {s.highlight}
                </div>
              )}

              {/* Step index at corner */}
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-50 text-slate-400 text-[10px] font-bold flex items-center justify-center border border-slate-100">
                {i + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Category chips */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <span className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mr-1">
            Categorias:
          </span>
          {['Personal Trainer', 'Fisioterapia', 'Nutrição', 'Psicologia', 'Estética', 'Massagem'].map((c) => (
            <span
              key={c}
              className="flex items-center gap-1 text-xs bg-teal-50 text-teal-800 font-semibold px-3 py-1.5 rounded-full border border-teal-100"
            >
              <Check className="w-3 h-3" />
              {c}
            </span>
          ))}
        </div>

        {/* CTA */}
        {onCreateAccount && (
          <div className="mt-10 text-center">
            <button
              onClick={onCreateAccount}
              data-testid="walkthrough-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              Quero um plano Vital Personal
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-slate-400 mt-3">
              A partir de <strong className="text-slate-700">R$ 469,90/mês</strong> · Cancele quando quiser
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingVitalPassWalkthrough;
